import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { useDebounceFn, useMemoizedFn } from 'ahooks';
import { isEmpty } from 'lodash-es';
import { useIntl, shallowEqual, useSelector } from 'umi';
import { store } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import { useImageCacheLocal } from '@/pages/aigc/hooks/useImageCacheLocal';
import { getAgentConfig } from '../../../../ChatPage/utils';
import { useConfirmDelete } from '../../../../ChatSmartTable/components/ExcelSheetTabs/hooks/useConfirmDelete';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { downloadFile } from '@home/utils/file';
import useChatShareHooks from '../../../../ChatShare/useChatShareHooks';
import { ChatShareDialog } from '../../../../ChatShare/types/chatShare';
import {
  IReportFileItem,
  TReportStepType,
} from '../../../../components/ReportViewer/types';
import {
  IMagicPosterPanelProps,
  EPosterActions,
  ISelectedStyle,
} from '../types';
import {
  POSTER_STEP_TYPE,
  POSTER_FILE_SUFFIX,
  POSTER_FILE_TYPE,
} from '../constants';
import {
  useMagicPosterFiles,
  TFileSuffix,
  IMagicPosterFileItem,
  normalizeFileSuffix,
} from './useMagicPosterFiles';
import { hasPosterDocument, findFrontendMetadataByFileId, computeStyleFromMetadata, getLatestPosterCosUrl } from '../../../utils';
import { usePosterAutoSave } from './usePosterAutoSave';
import { usePosterTemplateData } from '../../../../Home/hooks/usePosterTemplateData';

/** 面板激活状态（与 DS 报告 IActiveState 对齐，新增 fileActive 字段） */
interface IActiveState {
  /** 当前步骤页签：framework 内容规划 / poster 海报预览 */
  stepActive: TReportStepType;
  /** 当前选中的文件标识（非空说明用户主动切换过 tab 或 initPoster 已执行，用于缓存恢复判断） */
  fileActive: string;
}

/**
 * 用户交互模式：用于判断 loading 态的显示逻辑
 * - idle: 空闲状态
 * - poster_generating: 用户点击生成海报按钮后的状态
 * - file_switching: 用户手动切换文件后的状态
 */
type TInteractionMode = 'idle' | 'poster_generating' | 'file_switching';

/** Hook 返回类型 */
export interface IUseMagicPosterPanelReturn {
  /** 当前步骤页签 */
  stepActive: TReportStepType;
  /** 当前文件的 Markdown 内容 */
  currentFileContent: string;
  /** 文件列表加载状态 */
  loading: boolean;
  /** 海报加载状态（含文件加载和海报生成） */
  posterLoading: boolean;
  /** 适配后的文件列表（供选择器使用） */
  fileList: { documents?: IReportFileItem[]; };
  /** 当前选中的文件 */
  currentFile: IMagicPosterFileItem | undefined;
  /** 当前文件的签名 URL */
  currentFileSignedUrl: string;
  /** 主对话框中最新生成的海报图片原始 cos_url（用于判断当前是否在展示"最新海报"） */
  latestPosterCosUrl: string;
  /** 复用主对话框签名缓存得到的海报签名 URL（与正文图片同源，更稳定） */
  reusedPosterUrl: string;
  /** 海报页签是否禁用 */
  isDisabledPosterTab: boolean;
  /** 当前选中的样式 */
  selectedStyle: ISelectedStyle;
  /** 更新样式选择 */
  handleStyleChange: (style: ISelectedStyle) => void;
  /** 样式是否有效（可提交生成） */
  isStyleValid: boolean;
  /** Markdown 编辑器引用 */
  markdownRef: React.MutableRefObject<any>;
  /** 报告容器 DOM 引用 */
  reportRef: React.RefObject<HTMLDivElement>;
  /** 切换页签 */
  handleStepClick: (step: string) => void;
  /** 切换文件 */
  handleFileItemClick: (item: IReportFileItem, closeModal: () => void) => Promise<void>;
  /** 处理工具栏操作 */
  handleAction: (action: { key: string; }) => void;
  /** 导出海报图片 */
  handleExport: (type: string) => void;
  /** 关闭面板：先保存再隐藏 */
  handleClose: () => Promise<void>;
  /** 分享对话弹窗状态 */
  showShareChatDialog: ChatShareDialog | undefined;
  /** 设置分享对话弹窗状态 */
  setShowShareChatDialog: React.Dispatch<React.SetStateAction<ChatShareDialog | undefined>>;
  /** 刷新当前海报图片 URL（用于加载失败重试） */
  refreshPosterUrl: () => Promise<void>;
}

/** 魔法海报面板管理 Hook：统一管理页签切换、文件选择、自动保存、海报生成等 */
export const useMagicPosterPanel = ({
  sessionId,
  conversations,
  dsReport,
  streamingState,
  handleUpdateDsReport,
  startNewConversation,
  readonly = false,
}: IMagicPosterPanelProps): IUseMagicPosterPanelReturn => {
  const { formatMessage, locale } = useIntl();
  const userInfo = useSelector(
    (state: any) => state.home.userInfo,
    shallowEqual,
  );
  const isEn = locale === 'en-US';

  const [showShareChatDialog, setShowShareChatDialog] = useState<ChatShareDialog>();
  const { shareOnClickHandler } = useChatShareHooks();
  const [handleConfirmDelete] = useConfirmDelete();

  // 获取海报模板数据（用于动态查询 content_prompt 和 poster_prompt）
  const { data: posterTemplates } = usePosterTemplateData();

  const ACTIVE_CACHE_KEY = `__dba_poster_active_${dsReport?.messageId}__`;
  const DEFAULT_ACTIVE: IActiveState = { stepActive: 'framework', fileActive: '' };

  const [active, setActiveRaw] = useState<IActiveState>(() => {
    const isInCopilotChatBounce =
      store.getItem('__dba_from_copilot__', false) ||
      store.getItem('__dba_to_copilot__', false);
    if (isInCopilotChatBounce) {
      return store.getItem<IActiveState>(ACTIVE_CACHE_KEY, DEFAULT_ACTIVE) || DEFAULT_ACTIVE;
    }
    store.setItem(ACTIVE_CACHE_KEY, null);
    return DEFAULT_ACTIVE;
  });

  /** 设置页签激活状态，并同步到本地缓存（与 DS 报告 setActive 对齐） */
  const setActive = useMemoizedFn((update: Partial<IActiveState>) => {
    setActiveRaw((prev: IActiveState): IActiveState => {
      const next: IActiveState = {
        stepActive: update.stepActive ?? prev.stepActive,
        fileActive: update.fileActive ?? prev.fileActive,
      };
      store.setItem(ACTIVE_CACHE_KEY, next);
      return next;
    });
  });

  /** 用户交互模式：控制 loading 态显示逻辑 */
  const [interactionMode, setInteractionMode] = useState<TInteractionMode>('idle');

  const reportRef = useRef<HTMLDivElement>(null);
  const markdownRef = useRef<any>(null);
  const isStepSwitchingRef = useRef(false);

  const { stepActive } = active;
  const { sourceSessionId } = dsReport || {};

  const {
    fileList,
    currentFile,
    currentFileSignedUrl,
    currentFileContent,
    fetchFiles,
    switchFile,
    clearFiles,
    refreshCurrentFileUrl,
    isLoading: filesLoading,
  } = useMagicPosterFiles({ stepActive });

  // ==================== 复用主对话框已生成的海报图片 ====================
  // 左侧海报预览图片与主对话框使用同一文件(png cos_url)、共享同一签名缓存，
  // 既保证左右一致，又避免左侧再次走 get_excel_info 拉列表 + 签名造成的不稳定/不一致。
  const { getSignedUrl: getCachedSignedUrl } = useImageCacheLocal();
  /** 主对话框中最新生成的海报图片原始 cos_url */
  const latestPosterCosUrl = useMemo(
    () => getLatestPosterCosUrl(conversations),
    [conversations],
  );
  /** 复用主对话框的签名缓存得到的海报签名 URL（命中缓存时即时返回，与正文图片同源） */
  const [reusedPosterUrl, setReusedPosterUrl] = useState<string>('');
  useEffect(() => {
    let alive = true;
    if (!latestPosterCosUrl) {
      setReusedPosterUrl('');
      return;
    }
    getCachedSignedUrl(latestPosterCosUrl)
      .then((signed) => {
        if (alive) setReusedPosterUrl(signed || '');
      })
      .catch(() => {
        if (alive) setReusedPosterUrl('');
      });
    return () => {
      alive = false;
    };
  }, [latestPosterCosUrl, getCachedSignedUrl]);

  /** 从会话元数据中推导出的样式 */
  const derivedStyle = useMemo(() => {
    const metadata =
      findFrontendMetadataByFileId(conversations, currentFile?.id) ??
      dsReport?.frontend_metadata;
    
    // 兜底方案:根据当前文件 ID 查找对应 conversation 的 query
    const currentConversation = conversations?.find((c: any) => c?.messageId === currentFile?.id);
    const fallbackQuery = dsReport?.query || (currentConversation as any)?.query;
    
    return computeStyleFromMetadata(metadata, fallbackQuery);
  }, [conversations, currentFile?.id, dsReport?.frontend_metadata, dsReport?.query]);
  const [userOverrideStyle, setUserOverrideStyle] = useState<ISelectedStyle | null>(null);

  /** 兜底默认样式：第一个预设模板（稳定引用，仅模板数据变化时更新） */
  const defaultStyle = useMemo<ISelectedStyle | null>(() => {
    const firstTpl = posterTemplates?.[0]?.list?.[0];
    const firstId = firstTpl?.templateId || firstTpl?.id;
    return firstId ? { template: { id: firstId }, customInput: '' } : null;
  }, [posterTemplates]);

  /** 判断 derivedStyle 是否为有效选择 */
  const isDerivedValid = !!(
    (derivedStyle.template && !derivedStyle.template.isCustom) ||
    (derivedStyle.template?.isCustom && derivedStyle.customInput?.trim())
  );

  /** 最终生效的样式：userOverride > derivedStyle（有效时） > 第一个模板兜底 > derivedStyle（降级） */
  const selectedStyle = userOverrideStyle ?? (isDerivedValid ? derivedStyle : defaultStyle) ?? derivedStyle;
  /** 校验当前样式是否有效（流式未完成时无效，自定义样式需填入内容） */
  const isStyleValid = useMemo(() => {
    if (streamingState && streamingState !== 'complete') return false;
    if (selectedStyle.template?.isCustom) {
      return !!selectedStyle.customInput?.trim();
    }
    return true;
  }, [selectedStyle, streamingState]);

  /** 将文件列表适配为选择器所需的格式 */
  const adaptedFileListForSelector = useMemo(() => {
    if (isEmpty(fileList)) {
      return { documents: [] as IReportFileItem[] };
    }
    const documents: IReportFileItem[] = fileList.map((item) => ({
      id: item.id,
      title: item.name || '',
      display_title: item.name,
      version: '',
      date_time: item.update_time,
      cos_url: item.cos_url,
      file_suffix: item.file_suffix,
    }));
    return { documents };
  }, [fileList]);

  const isStreaming = !!streamingState && streamingState !== 'complete';

  /** 海报是否正在生成中 */
  const posterGenerating = useMemo(() => {
    // 流式完成且已有海报，则不是生成中
    const latestHasPoster = hasPosterDocument(conversations, true);
    if (latestHasPoster && !isStreaming) return false;
    // 手动选择文件时，不显示生成中
    if (interactionMode === 'file_switching') return false;
    // 发起了海报请求且流式未完成
    if (interactionMode === 'poster_generating' && isStreaming) return true;
    // 在海报页签且流式进行中
    return stepActive === POSTER_STEP_TYPE.POSTER && isStreaming;
  }, [conversations, interactionMode, stepActive, isStreaming]);

  /** 流式完成时重置交互模式 */
  useEffect(() => {
    if (interactionMode !== 'idle' && !isStreaming) {
      setInteractionMode('idle');
    }
  }, [interactionMode, isStreaming]);

  /** 海报页签是否应禁用（仅当无海报且文件后缀为 MD/PNG 时禁用） */
  const isDisabledPosterTab = useMemo(() => {
    if (posterGenerating || interactionMode === 'poster_generating') return false;
    if (isStreaming) return false;
    const hasAnyPoster = hasPosterDocument(conversations);
    if (hasAnyPoster) return false;
    return !!(dsReport?.id && (normalizeFileSuffix(dsReport.file_suffix) === POSTER_FILE_SUFFIX.MD || normalizeFileSuffix(dsReport.file_suffix) === POSTER_FILE_SUFFIX.PNG));
  }, [dsReport?.id, dsReport?.file_suffix, conversations, interactionMode, posterGenerating, isStreaming]);

  /** 用户手动更新样式选择 */
  const handleStyleChange = useCallback(
    (style: ISelectedStyle) => {
      setUserOverrideStyle(style);
    },
    []
  );

  // 初始化/切换 messageId 时重置页签（与 DS 报告完全对齐）
  // 无条件重置，缓存恢复由下方 initPoster effect 负责覆盖
  useEffect(() => {
    setActive({ stepActive: 'framework', fileActive: '' });
  }, [dsReport?.messageId]);
  
  /** 切换文件时重置用户手动选择的风格，回落到 derivedStyle */
  useEffect(() => {
    setUserOverrideStyle(null);
  }, [currentFile?.id]);

  // 初始化海报数据（与 DS 报告 initDocument 完全对齐）
  // 缓存有 fileActive 说明用户主动切换过 tab 或 initPoster 已执行，优先使用缓存
  useEffect(() => {
    const initPoster = async (): Promise<void> => {
      if (!dsReport?.id || !sessionId) return;

      try {
        // 与 DS 报告 initDocument（第 360-365 行）完全对齐：
        // 直接从 store 读最新缓存，避免 useEffect 闭包读到旧值
        // 缓存有 fileActive 说明用户主动切换过 tab，优先使用；否则使用服务端 file_suffix
        const cachedActive = store.getItem<IActiveState | null>(ACTIVE_CACHE_KEY, null);
        const normalizedSuffix = normalizeFileSuffix(dsReport?.file_suffix);
        const initType: TReportStepType =
          cachedActive?.fileActive
            ? cachedActive.stepActive
            : (normalizedSuffix === POSTER_FILE_SUFFIX.PNG
              ? POSTER_STEP_TYPE.POSTER
              : POSTER_STEP_TYPE.FRAMEWORK);

        // 类型不一致时（用户在 Chat↔Copilot 期间手动换过 tab），
        // document_id 无法在 initType 列表中匹配；先拉 initType 列表取第一个文件
        const currentFileSuffix = initType === POSTER_STEP_TYPE.POSTER
          ? POSTER_FILE_SUFFIX.PNG
          : POSTER_FILE_SUFFIX.MD;
        const fetchResult = await fetchFiles({
          sessionId: sourceSessionId || sessionId,
          targetFileId: dsReport?.id,
          defaultFileSuffix: currentFileSuffix,
        });

        // 与 DS 报告对齐：从 fetchFiles 返回值中提取文件 ID，不依赖 currentFile（可能陈旧）
        // fetchFiles 返回 result（含 files），从中找到匹配 targetFileId 的文件 ID
        const files = fetchResult?.files || [];
        const matchedWrapper = dsReport?.id
          ? files.find((f: any) => {
            const latestId = f?.latest_file?.id;
            const originalId = f?.original_file?.id;
            return latestId === dsReport.id || originalId === dsReport.id;
          })
          : files[0];
        const targetFileId = matchedWrapper?.latest_file?.id || matchedWrapper?.original_file?.id || '';

        // 设置 stepActive 与 initType 一致，覆盖上方 useEffect 的无条件重置
        // 同时设置 fileActive 为当前文件 id，标记缓存已生效
        setActive({ stepActive: initType, fileActive: targetFileId });
        markdownRef.current?.resetDirty?.();
      } catch (error) {
        console.error('Error initializing poster:', error);
      }
    };
    initPoster();
  }, [dsReport?.file_suffix, dsReport?.id, dsReport?.messageId]);

  // ==================== 自动保存（仅内容规划页签） ====================

  const { flushSave } = usePosterAutoSave({
    currentFileContent,
    currentFile,
    currentFileSignedUrl,
    stepActive,
    readonly,
    markdownRef,
  });

  /** 切换文件（与 DS 报告 handleFileItemClick 对齐） */
  const handleFileItemClick = useCallback(
    async (
      item: IReportFileItem,
      closeModal: () => void
    ): Promise<void> => {
      if (item.id === currentFile?.id) {
        closeModal();
        return;
      }

      const targetFile = fileList.find((f) => f.id === item.id);
      if (!targetFile) {
        closeModal();
        return;
      }

      setInteractionMode('file_switching');
      await flushSave();
      await switchFile(targetFile);

      // 切换文件时设置 fileActive，标记用户已主动操作（用于缓存恢复判断）
      setActive({ fileActive: item.id });

      buriedBtnPoint({
        pageId: '700501',
        buttonId: '700501057',
        extInfo: JSON.stringify({
          ...item,
          message: stepActive,
          type: POSTER_FILE_TYPE,
        }),
        typeId: 'aigc',
      });
      closeModal();
    },
    [currentFile, fileList, stepActive, switchFile, flushSave, setActive]
  );

  /** 切换页签（与 DS 报告 handleStepClick 对齐） */
  const handleStepClick = useMemoizedFn(async (step: string) => {
    if (step === stepActive) return;
    if (isStepSwitchingRef.current) return;
    isStepSwitchingRef.current = true;

    try {
      setInteractionMode('idle');
      setUserOverrideStyle(null); // 切换页签时重置用户手动选择的风格，回落到 derivedStyle
      await flushSave();
      // 切换页签时同时设置 fileActive，标记用户已主动切换（用于缓存恢复判断）
      setActive({ stepActive: step as TReportStepType, fileActive: currentFile?.id || '' });

      const targetFileSuffix: TFileSuffix = step === POSTER_STEP_TYPE.POSTER ? POSTER_FILE_SUFFIX.PNG : POSTER_FILE_SUFFIX.MD;
      await fetchFiles({
        sessionId: sourceSessionId || sessionId,
        targetFileId: currentFile?.id,
        defaultFileSuffix: targetFileSuffix,
      });

      buriedBtnPoint({
        pageId: '700501',
        buttonId: '700501056',
        extInfo: JSON.stringify({
          message: step,
          type: POSTER_FILE_TYPE,
        }),
        typeId: 'aigc',
      });
    } catch (error) {
      console.error('Error in poster handleStepClick:', error);
    } finally {
      isStepSwitchingRef.current = false;
    }
  });

  /** 重置到原始文件（与 DS 报告 handleReset 对齐） */
  const handleReset = useCallback(async () => {
    const fileId = currentFile?.id || dsReport?.id;
    if (!fileId) {
      console.warn('未找到文件 ID，无法重置');
      return;
    }
    const targetFileSuffix: TFileSuffix = stepActive === POSTER_STEP_TYPE.POSTER ? POSTER_FILE_SUFFIX.PNG : POSTER_FILE_SUFFIX.MD;
    await fetchFiles({
      sessionId: sourceSessionId || sessionId,
      type: 'original',
      targetFileId: fileId,
      defaultFileSuffix: targetFileSuffix,
    });
    markdownRef.current?.handleSetHtml?.();
    // 重置后回到 framework 页签，并清除 fileActive 缓存标记
    setActive({ stepActive: POSTER_STEP_TYPE.FRAMEWORK, fileActive: '' });
  }, [currentFile?.id, dsReport?.id, fetchFiles, sessionId, sourceSessionId, markdownRef, stepActive, setActive]);

  /** 生成魔法海报：先保存 → 构建请求参数 → 发起新会话 */
  const handleGeneratePoster = useCallback(async (isRegenerate = false) => {
    // 校验样式是否有效：自定义模板需要填入内容
    if (!isStyleValid) {
      console.warn('样式无效，无法生成海报');
      return;
    }

    await flushSave();

    const { selectedAgentId, currentMode, chatEngine } = getAgentConfig({
      userId: userInfo?.openid,
    });
    
    const actionId = isRegenerate ? 'dba_magic_poster_regenerate' : 'dba_magic_poster_confirm_generate';

    const isCustomStyle = selectedStyle.template?.isCustom;
    const posterTemplate = !selectedStyle.template ? 0 : isCustomStyle ? 2 : 1;

    const functionParams: Record<string, any> = {
      name: POSTER_FILE_TYPE,
      source: 'button_gp_chat',
    };

    // 通过 templateId 动态查询模板数据（用于生成请求参数）
    // 首次生成和重新生成都使用用户当前界面选择的样式（selectedStyle）
    let contentPrompt = '';
    let posterPrompt = '';

    if (isCustomStyle) {
      // 自定义模式：content_prompt 和 poster_prompt 都使用用户输入的 customInput
      contentPrompt = selectedStyle.customInput;
      posterPrompt = selectedStyle.customInput;
    } else if (selectedStyle.template?.id && posterTemplates?.[0]?.list) {
      // 预设模板：动态查询模板数据
      const template = posterTemplates[0].list.find((tpl: any) => 
        (tpl.templateId || tpl.id) === selectedStyle.template?.id
      );
      
      if (template) {
        contentPrompt = isEn ? template.content_prompt_en : template.content_prompt_cn;
        posterPrompt = isEn ? template.poster_prompt_en : template.poster_prompt_cn;
      }
    }

    // 解析 input_md_id / input_md_url：永远使用 md 文件的 id 和签名 URL
    // - 内容规划页签：currentFile 本身就是 md，直接使用
    // - 海报预览页签：currentFile 是 png，走 fetchFiles 的 silent 旁路模式
    //   按 wrapper group_id 在 md 列表里匹配同一任务；未命中回退到第一个 md
    let inputMdId: string | undefined = currentFile?.id;
    let inputMdUrl: string = currentFileSignedUrl;

    const isCurrentMd = currentFile?.file_suffix === POSTER_FILE_SUFFIX.MD;
    if (!isCurrentMd) {
      const md = await fetchFiles({
        sessionId: sourceSessionId || sessionId,
        defaultFileSuffix: POSTER_FILE_SUFFIX.MD,
        silent: true,
        groupId: currentFile?.group_id,
      });
      if (md?.id && md?.signedUrl) {
        inputMdId = md.id;
        inputMdUrl = md.signedUrl;
      }
    }

    functionParams.args = {
      poster_template: posterTemplate,
      input_md_id: inputMdId,
      input_md_url: inputMdUrl,
      content_prompt: contentPrompt,
      poster_prompt: posterPrompt,
    };

    // 简化 frontend_metadata：只传递必要字段
    const frontendMetadata: Record<string, any> = {
      type: 'magic_poster',
      poster_template: posterTemplate,
    };

    // 预设模板：只传递 templateId
    if (!isCustomStyle && selectedStyle.template?.id) {
      frontendMetadata.templateId = selectedStyle.template.id;
    }

    const serverParams = {
      model_id: selectedAgentId,
      mode: currentMode,
      ...(chatEngine && { chat_engine: chatEngine }),
      system_language: locale,
      function: functionParams,
      frontend_metadata: frontendMetadata,
    };

    const logParams = {
      dataSource: 'agent',
      actionType: 'poster_generate',
      name: POSTER_FILE_TYPE,
    };

    const queryMessage = isCustomStyle
      ? `${selectedStyle.customInput}, ${formatMessage({ id: actionId })}`
      : formatMessage({ id: actionId });

    setUserOverrideStyle(null);
    setInteractionMode('poster_generating');
    // 生成海报时切换到 poster 页签，同时设置 fileActive 标记（用于缓存恢复判断）
    setActive({ stepActive: POSTER_STEP_TYPE.POSTER, fileActive: currentFile?.id || '' });

    // 判断当前会话是否已有海报图片
    const hasExistingPoster = hasPosterDocument(conversations);
    if (hasExistingPoster) {
      // 重新生成场景：已有海报，重新获取文件列表（类似页面初始化）
      fetchFiles({
        sessionId: sourceSessionId || sessionId,
        defaultFileSuffix: POSTER_FILE_SUFFIX.PNG,
      });
    } else {
      // 初次生成场景：无海报，清空文件下拉列表数据
      clearFiles();
    }
    await startNewConversation(queryMessage, [], {}, {
      from: 'send',
      serverParams,
      logParams,
    });

    // 准备发送埋点数据
    buriedBtnPoint({
      pageId: '700501',
      buttonId: isRegenerate ? '700501054' : '700501055',
      extInfo: JSON.stringify({
        message: formatMessage({ id: actionId }),
        type: POSTER_FILE_TYPE,
      }),
      typeId: 'aigc',
    });
  }, [
    userInfo?.openid, selectedStyle, dsReport?.frontend_metadata, currentFile, currentFileSignedUrl,
    locale, isEn, startNewConversation, flushSave, isStyleValid,
    conversations, sourceSessionId, sessionId, fetchFiles, clearFiles, posterTemplates,
  ]);

  /** 导出海报图片：通过 Canvas 转换为指定格式（PNG / JPG）后下载 */
  const handleExport = useCallback(
    (type: string) => {
      const imageUrl = currentFileSignedUrl;
      if (!imageUrl) {
        console.warn('导出失败：当前没有可用的图片 URL');
        return;
      }

      const ext = type === 'jpg' ? 'jpg' : 'png';
      const fileName = `${currentFile?.name || 'magic-poster'}.${ext}`;
      const mimeType = type === 'jpg' ? 'image/jpeg' : 'image/png';

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0);

        canvas.toBlob(
          (blob) => {
            if (!blob) return;
            downloadFile(blob, fileName);
          },
          mimeType,
          type === 'jpg' ? 0.92 : undefined,
        );
      };
      img.onerror = () => {
        console.error('导出失败：图片加载失败，尝试直接下载原图', imageUrl);
        // 降级方案：直接下载原图（不转格式）
        downloadFile(imageUrl, fileName);
      };
      // 添加时间戳避免浏览器缓存非 CORS 响应导致加载失败
      const separator = imageUrl.includes('?') ? '&' : '?';
      img.src = `${imageUrl}${separator}_t=${Date.now()}`;
    },
    [currentFileSignedUrl, currentFile?.name],
  );

  /** 处理工具栏操作：重置 / 生成海报 / 分享 */
  const { run: handleAction } = useDebounceFn(
    async ({ key }: { key: string; }) => {
      switch (key) {
        case EPosterActions.FrameworkReset:
          await handleConfirmDelete({
            mutate: async () => {
              await handleReset();
            },
            caption: formatMessage({ id: 'dba_ds_report_reset' }),
            message: formatMessage({ id: 'dba_ds_report_reset_content' }),
            okText: formatMessage({ id: 'dba_confirm' }),
            cancelText: formatMessage({ id: 'dba_cancel' }),
            showNotification: true,
            successMessage: formatMessage({ id: 'dba_ds_report_reset_success' }),
          });
          break;
        case EPosterActions.GeneratePoster:
          await handleGeneratePoster(false);
          break;
        case EPosterActions.Regenerate:
          await handleGeneratePoster(true);
          break;
        case EPosterActions.Share:
          shareOnClickHandler(sessionId, setShowShareChatDialog);
          break;
        default:
          console.log('未处理的操作:', key);
          break;
      }
    },
    { wait: 300, leading: true }
  );

  /** 关闭面板：先保存再隐藏 */
  const handleClose = useCallback(async () => {
    await flushSave({
      forceStepActive: stepActive,
    });
    handleUpdateDsReport?.({ show: false });
  }, [flushSave, handleUpdateDsReport, stepActive]);

  return {
    stepActive,
    currentFileContent,
    loading: filesLoading,
    posterLoading: filesLoading || posterGenerating,
    fileList: adaptedFileListForSelector,
    currentFile,
    currentFileSignedUrl,
    latestPosterCosUrl,
    reusedPosterUrl,
    isDisabledPosterTab,
    selectedStyle,
    handleStyleChange,
    isStyleValid,
    markdownRef,
    reportRef,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleStepClick,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleFileItemClick,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleAction,
    handleExport,
    handleClose,
    showShareChatDialog,
    setShowShareChatDialog,
    refreshPosterUrl: refreshCurrentFileUrl,
  };
};

export default useMagicPosterPanel;
